/** Copiloto de IA: sugestões da conversa (o tempo real atualiza a chave). */
import type { AcaoCopiloto, AvaliacaoIa, EstadoCopiloto, SugestoesIa } from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ErroApi } from "./api";

export const chaveCopiloto = (conversaId: string) => ["conversa", conversaId, "copiloto"];

export function useCopiloto(conversaId: string) {
  return useQuery({
    queryKey: chaveCopiloto(conversaId),
    queryFn: () => api.get<EstadoCopiloto>(`/conversas/${conversaId}/copiloto`),
    staleTime: 30_000,
  });
}

export function useAcoesCopiloto(conversaId: string) {
  const qc = useQueryClient();
  const definir = (atual: SugestoesIa | null) =>
    qc.setQueryData<EstadoCopiloto>(chaveCopiloto(conversaId), (e) => (e ? { ...e, atual } : e));
  const erro = (e: unknown) =>
    toast.error(e instanceof ErroApi ? e.message : "A IA não respondeu agora.");

  const pedir = useMutation({
    mutationFn: (v: { acao: AcaoCopiloto; base?: string | null }) =>
      api.post<SugestoesIa>(`/conversas/${conversaId}/copiloto/sugestoes`, v),
    onSuccess: definir,
    onError: erro,
  });
  const resumir = useMutation({
    mutationFn: () => api.post<{ resumo: string }>(`/conversas/${conversaId}/copiloto/resumo`),
    onError: erro,
  });
  const descartar = useMutation({
    mutationFn: (interacaoId: string) =>
      api.post(`/conversas/${conversaId}/copiloto/sugestoes/${interacaoId}/descartar`),
    onMutate: () => definir(null),
  });
  const avaliar = useMutation({
    mutationFn: (v: { interacaoId: string; avaliacao: AvaliacaoIa }) =>
      api.post(`/conversas/${conversaId}/copiloto/sugestoes/${v.interacaoId}/avaliacao`, {
        avaliacao: v.avaliacao,
      }),
    onSuccess: () => toast.success("Obrigado! Isso ajuda a melhorar as sugestões."),
    onError: erro,
  });
  return { pedir, resumir, descartar, avaliar };
}

/** Gestão avalia uma resposta real (ótima vira exemplo para a IA). */
export function useAvaliarResposta() {
  return useMutation({
    mutationFn: (v: { mensagemId: string; avaliacao: AvaliacaoIa }) =>
      api.post(`/mensagens/${v.mensagemId}/avaliacao`, { avaliacao: v.avaliacao }),
    onSuccess: (_r, v) =>
      toast.success(
        v.avaliacao === "otima" || v.avaliacao === "resposta_padrao"
          ? "Marcada como exemplo para as sugestões da IA."
          : "Avaliação registrada.",
      ),
    onError: (e) => toast.error(e instanceof ErroApi ? e.message : "Não foi possível avaliar."),
  });
}
